import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import type { CSSProperties, ReactNode } from "react";
import { T } from "./pricing-shared";

/** Identifier for the at-most-one open info popover. */
export type InfoId =
  | "ki"
  | "epost"
  | "tel"
  | "auf"
  | "datei"
  | "post"
  | "ms365"
  | "it"
  | "dms";

const POPO_BASE: CSSProperties = {
  position: "absolute",
  zIndex: 60,
  background: T.popover,
  border: "1px solid rgba(255,255,255,0.10)",
  borderRadius: 16,
  boxShadow:
    "0 26px 60px -20px rgba(11,12,8,0.6), 0 8px 22px -10px rgba(11,12,8,0.45)",
};

const HEAD: CSSProperties = {
  fontFamily: T.sans,
  fontWeight: 600,
  color: T.popoverText,
  lineHeight: 1.32,
};

/** Info "ⓘ" toggle button; icon turns verdigris while its popover is open. */
export function InfoButton({
  open,
  onToggle,
  label,
}: {
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly label: string;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-info-keep="1"
      aria-label={label}
      className="mkpreise-iconbtn"
      style={{
        appearance: "none",
        WebkitAppearance: "none",
        border: "none",
        background: "none",
        padding: 2,
        margin: 0,
        cursor: "pointer",
        lineHeight: 0,
        display: "inline-flex",
        color: open ? T.accent : "#9AA0A6",
      }}
    >
      <svg
        width={15}
        height={15}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: "block" }}
        aria-hidden="true"
      >
        <circle cx={12} cy={12} r={10} />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    </button>
  );
}

/** Title text with its ⓘ glued to the last word, so a wrap never strands the icon. */
export function TitleWithInfo({
  title,
  style,
  info,
}: {
  readonly title: string;
  readonly style: CSSProperties;
  readonly info: ReactNode;
}): ReactNode {
  const cut = title.lastIndexOf(" ") + 1;
  return (
    <span style={style}>
      {title.slice(0, cut)}
      <span style={{ whiteSpace: "nowrap" }}>
        {title.slice(cut)}
        <span
          style={{
            display: "inline-block",
            marginLeft: 6,
            verticalAlign: "middle",
            lineHeight: 0,
          }}
        >
          {info}
        </span>
      </span>
    </span>
  );
}

/** Dark popover shell with the shared close "×" button. */
export function DarkPopover({
  onClose,
  width,
  position,
  padding = "21px 23px 23px",
  children,
}: {
  readonly onClose: () => void;
  readonly width: CSSProperties["width"];
  readonly position: CSSProperties;
  readonly padding?: string;
  readonly children: ReactNode;
}): ReactNode {
  const t = useTranslations("public.pricingPopovers");
  return (
    <div
      data-info-keep="1"
      style={{ ...POPO_BASE, ...position, width, padding }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t("schliessen")}
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          appearance: "none",
          border: "none",
          background: "none",
          padding: 4,
          cursor: "pointer",
          color: "rgba(244,242,236,0.42)",
          lineHeight: 0,
          display: "inline-flex",
        }}
      >
        <X size={15} strokeWidth={1.8} aria-hidden="true" />
      </button>
      {children}
    </div>
  );
}

function P({
  children,
  margin = "0 0 12px",
  size = 14,
}: {
  readonly children: ReactNode;
  readonly margin?: string;
  readonly size?: number;
}): ReactNode {
  return (
    <p
      style={{
        fontFamily: T.sans,
        fontSize: size,
        lineHeight: 1.56,
        color: "rgba(244,242,236,0.6)",
        margin,
      }}
    >
      {children}
    </p>
  );
}

function B({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <strong style={{ color: T.popoverText, fontWeight: 600 }}>
      {children}
    </strong>
  );
}

/** `<b>…</b>` inside a message renders as the popover's bold accent. */
const RICH_TAGS = {
  b: (chunks: ReactNode): ReactNode => <B>{chunks}</B>,
};

/** The verbatim body of each info popover, keyed by id. */
export function popoverBody(id: InfoId): ReactNode {
  return <PopoverBody id={id} />;
}

/**
 * Rendered body of one info popover.
 *
 * A component rather than a plain function because `popoverBody` is called
 * conditionally at its call sites — a hook called from there would change
 * position between renders.
 */
function PopoverBody({ id }: { readonly id: InfoId }): ReactNode {
  const t = useTranslations("public.pricingPopovers");
  switch (id) {
    case "ki":
      return (
        <>
          <div style={{ ...HEAD, fontSize: 16.5, margin: "0 24px 11px 0" }}>
            {t("keineSorgeKiGuenstig")}
          </div>
          <P>{t.rich("normalenHausverwaltungMeist10", RICH_TAGS)}</P>
          <P margin="0">{t.rich("vielenAgentenSpaeterMal", RICH_TAGS)}</P>
          <div
            style={{
              height: 1,
              background: "rgba(244,242,236,0.1)",
              margin: "15px 0",
            }}
          />
          <P margin="0">{t.rich("musstKiFeaturesNicht", RICH_TAGS)}</P>
        </>
      );
    case "epost":
      return (
        <>
          <div style={{ ...HEAD, fontSize: 16.5, margin: "0 24px 11px 0" }}>
            {t("echteBriefePerE")}
          </div>
          <P>{t("andereAnbieterVerlangenFuer")}</P>
          <P margin="0">{t.rich("nichtKosten11", RICH_TAGS)}</P>
        </>
      );
    case "tel":
      return (
        <>
          <div style={{ ...HEAD, fontSize: 16.5, margin: "0 24px 11px 0" }}>
            {t("ueberTwilioSelbstkosten")}
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(63,200,166,0.12)",
              border: "1px solid rgba(63,200,166,0.32)",
              borderRadius: 999,
              padding: "5px 11px",
              margin: "0 0 13px",
            }}
          >
            <svg
              width={13}
              height={13}
              viewBox="0 0 24 24"
              fill="none"
              stroke={T.mint}
              strokeWidth={2.4}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <span
              style={{
                fontFamily: T.label,
                fontSize: 10.5,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: T.mint,
              }}
            >
              {t("dsgvoKonform")}
            </span>
          </div>
          <P>{t.rich("telefonieLaeuftUeberTwilio", RICH_TAGS)}</P>
          <P>{t.rich("tarifeVergleichbarTelekomVodafone", RICH_TAGS)}</P>
          <P margin="0">{t("bestehendeRufnummernLassenTwilio")}</P>
        </>
      );
    case "auf":
      return (
        <>
          <P margin="0 24px 13px 0">
            {t.rich("keineSorgeKeineKostenfalle", RICH_TAGS)}
          </P>
          <div style={{ ...HEAD, fontSize: 14.5, margin: "0 0 7px" }}>
            {t("kommentar")}
          </div>
          <P margin="0 0 13px">{t.rich("kommentarEMailInterne", RICH_TAGS)}</P>
          <div
            style={{
              border: "1px solid rgba(244,242,236,0.13)",
              borderRadius: 11,
              padding: "13px 15px",
              margin: "0 0 14px",
            }}
          >
            <div
              style={{
                fontFamily: T.label,
                fontSize: 10,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: T.mint,
                margin: "0 0 8px",
              }}
            >
              {t("beispiel")}
            </div>
            <div
              style={{
                fontFamily: T.sans,
                fontSize: 14,
                lineHeight: 1.5,
                color: "rgba(244,242,236,0.66)",
              }}
            >
              {t.rich("hausverwaltung800Einheiten", RICH_TAGS)}
            </div>
            <div
              style={{
                fontFamily: T.sans,
                fontSize: 14,
                lineHeight: 1.5,
                color: "rgba(244,242,236,0.66)",
                marginTop: 3,
              }}
            >
              {t.rich("k8005040000", RICH_TAGS)}
            </div>
          </div>
          <div style={{ ...HEAD, fontSize: 14.5, margin: "0 0 7px" }}>
            {t("warumNichtUnbegrenzt")}
          </div>
          <P margin="0 0 13px">{t("ueberUnserenEMail")}</P>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            {(
              [
                "spamAutomatischeLoeschungsregelnHelfen",
                "abSolltestAusmisten",
              ] as const
            ).map((line) => (
              <div
                key={line}
                style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
              >
                <span
                  style={{
                    color: T.mint,
                    fontSize: 15,
                    lineHeight: 1.4,
                    flex: "none",
                  }}
                >
                  ·
                </span>
                <span
                  style={{
                    fontFamily: T.sans,
                    fontSize: 14,
                    lineHeight: 1.5,
                    color: "rgba(244,242,236,0.62)",
                  }}
                >
                  {t(line)}
                </span>
              </div>
            ))}
          </div>
        </>
      );
    case "datei":
      return (
        <>
          <div style={{ ...HEAD, fontSize: 16.5, margin: "0 24px 11px 0" }}>
            {t("k100GbReichenFast")}
          </div>
          <P margin="0">{t.rich("keineSorgeMehrGenug", RICH_TAGS)}</P>
        </>
      );
    case "post":
      return (
        <>
          <div style={{ ...HEAD, fontSize: 16.5, margin: "0 24px 11px 0" }}>
            {t("brauchstWirklichMehr")}
          </div>
          <P margin="0 0 13px">{t("unsereEmpfehlungVerbindeNicht")}</P>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 9,
              margin: "0 0 14px",
            }}
          >
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span
                style={{
                  color: T.mint,
                  fontSize: 15,
                  lineHeight: 1.4,
                  flex: "none",
                }}
              >
                ·
              </span>
              <span
                style={{
                  fontFamily: T.label,
                  fontSize: 13,
                  lineHeight: 1.4,
                  color: T.mint,
                }}
              >
                {t("infoRechnungMietverwaltung")}
              </span>
            </div>
            {(["geschaeftsfuehrung", "teamleitung"] as const).map((line) => (
              <div
                key={line}
                style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
              >
                <span
                  style={{
                    color: T.mint,
                    fontSize: 15,
                    lineHeight: 1.4,
                    flex: "none",
                  }}
                >
                  ·
                </span>
                <span
                  style={{
                    fontFamily: T.sans,
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: "rgba(244,242,236,0.7)",
                  }}
                >
                  {t(line)}
                </span>
              </div>
            ))}
          </div>
          <P margin="0">{t("aktuellZahlenAnbieterPro")}</P>
        </>
      );
    case "ms365":
      return (
        <>
          <div style={{ ...HEAD, fontSize: 16, margin: "0 22px 10px 0" }}>
            {t("microsoft365BleibtTeilweise")}
          </div>
          <P size={13.5} margin="0 0 11px">
            {t.rich("eMailKannstNicht", RICH_TAGS)}
          </P>
          <P size={13.5} margin="0">
            {t.rich("guenstigeMailadressenZB", RICH_TAGS)}
          </P>
        </>
      );
    case "it":
      return (
        <>
          <div style={{ ...HEAD, fontSize: 16, margin: "0 22px 10px 0" }}>
            {t("brauchst")}
          </div>
          <P size={13.5} margin="0 0 11px">
            {t("itDienstleisterErsetzenNatuerlich")}
          </P>
          <P size={13.5} margin="0 0 11px">
            {t.rich("fallenEigeneServerTelefonanlage", RICH_TAGS)}
          </P>
          <P size={13.5} margin="0">
            {t.rich("wirklichEinfachGeradeKi", RICH_TAGS)}
          </P>
        </>
      );
    case "dms":
      return (
        <>
          <div style={{ ...HEAD, fontSize: 16, margin: "0 22px 10px 0" }}>
            {t("server")}
          </div>
          <P size={13.5} margin="0 0 11px">
            {t("fuerAlleKeinDokumentenmanagementsystem")}
          </P>
          <P size={13.5} margin="0 0 11px">
            {t.rich("laeuftServerBuchhaltungKannst", RICH_TAGS)}
          </P>
          <P size={13.5} margin="0">
            {t.rich("dientServerDeinenDokumenten", RICH_TAGS)}
          </P>
        </>
      );
  }
}
